import { Event } from "../../../../pojo/Event";
import { Item } from "../../../../pojo/Item";
import { Category } from "../../../../pojo/Category";
import { S25Util } from "../../../../util/s25-util";
import { StudentEngagementMyOrgsSearchParams } from "../../student.engagement.service";
import { ObjectSearchFilter } from "./se.object.search.filter.component";
import { CustomAttributes } from "../../../../pojo/CustomAttributes";
import { Organization } from "../../../../pojo/Organization";
import { SearchCriteria } from "../../../../pojo/SearchCriteria";
import { AdvancedSearchUtil } from "../../../advanced-search/advanced-search-util";
import { attendanceState } from "../../attendance/attendance.service";
import StepType = SearchCriteria.StepType;

const PAST_DEFAULT_MONTHS = -3;

export type SearchParams = Event.WsQueryParams | Organization.WsQueryParams;
type EventSearchModelContext = {
    userContactId?: number;
};

export type EventReportDateRange = {
    startDate: string;
    endDate: string;
};

export class SeSearchUtil {
    public static baseOrgParams: StudentEngagementMyOrgsSearchParams = {
        attribute_id: CustomAttributes.system.StudentEngagementOrg.id,
        attribute_value: "true",
    };

    static buildSearchParams(
        filter: ObjectSearchFilter,
        itemTypeId: Item.Ids,
        options?: {
            isAdmin?: boolean;
            myOrgs?: boolean;
        },
    ): Promise<SearchParams | undefined> | undefined {
        if (!itemTypeId) return;
        switch (itemTypeId) {
            case Item.Ids.Event:
                return Promise.resolve(SeSearchUtil.buildEventSearchParams(filter));
            case Item.Ids.Organization:
                return SeSearchUtil.buildOrgSearchParams(filter, options);
        }
    }

    /**
     * Builds a searchCriteria model from SE formatted search.
     * This model can easily be converted to step_ids
     * @param filter
     * @param context
     * @returns
     */
    static buildEventSearchModel(
        filter: ObjectSearchFilter,
        context: EventSearchModelContext = {},
    ): SearchCriteria.Model {
        const querySteps: SearchCriteria.Step[] = [];

        //Is Student Engagment event add this to all S25 based queries
        const categoriesStep = AdvancedSearchUtil.getStep(Item.Ids.Event, StepType.Event.Categories);
        categoriesStep.step_param = [
            { itemId: Category.SystemDefined.StudentEngagement, itemName: "Student Engagement" },
        ];
        querySteps.push(categoriesStep);

        //Promoted events - seperate step but could be added to the above step if there's a reason
        if (filter.eventType === "promoted") {
            const promotedStep = AdvancedSearchUtil.getStep(Item.Ids.Event, StepType.Event.Categories);
            promotedStep.step_param = [
                { itemId: Category.SystemDefined.Connect25EventPromoted, itemName: "Connect25 Promoted" },
            ];
            querySteps.push(promotedStep);
        }

        //Keyword
        const searchTerm = filter.searchTerm?.trim();
        if (searchTerm) {
            const keywordStep = AdvancedSearchUtil.getStep(Item.Ids.Event, StepType.Event.Keyword);
            keywordStep.step_param = [{ itemName: searchTerm }];
            querySteps.push(keywordStep);
        }

        // Organizations by organization_id (step 110 — not 113, which expects organization_search_id)
        const organizationIds =
            filter.organizations
                ?.map((organization) => Number(organization.itemId))
                .filter((organizationId) => S25Util.toInt(organizationId)) ?? [];
        if (organizationIds.length > 0) {
            const organizationNameById = new Map(
                filter.organizations?.map((organization) => [Number(organization.itemId), organization.itemName]) ?? [],
            );
            const organizationsStep = AdvancedSearchUtil.getStep(Item.Ids.Event, StepType.Event.Organizations);
            organizationsStep.step_param = organizationIds.map((organizationId) => ({
                itemId: organizationId,
                itemName: organizationNameById.get(organizationId) ?? String(organizationId),
            }));
            querySteps.push(organizationsStep);
        }

        //User's attending status
        if (filter.eventType === "attending" && context.userContactId) {
            const attendeesStep = AdvancedSearchUtil.getStep(Item.Ids.Event, StepType.Event.Attendee);
            attendeesStep.step_param = [{ itemId: context.userContactId, itemName: String(context.userContactId) }];
            querySteps.push(attendeesStep);
        }

        return {
            query_method: "all",
            step: querySteps,
        };
    }

    static resolveEventReportDateRange(filter: ObjectSearchFilter): EventReportDateRange {
        const today = S25Util.date.currentDate();
        const todayPlusOneYear = S25Util.date.addYears(today, 1);
        const epochStartDate = new Date(2000, 0, 1);
        const eventDateRange = SeSearchUtil.resolveEventDateRange(filter);
        const startDate = eventDateRange.startDate ?? epochStartDate;
        const endDate = eventDateRange.endDate ?? todayPlusOneYear;
        return {
            startDate: S25Util.date.toS25ISODateStr(startDate)!, //we're setting dates above so can be confident in non-null assertion here
            endDate: S25Util.date.toS25ISODateStr(endDate)!,
        };
    }

    private static resolveEventDateRange(filter: ObjectSearchFilter): { startDate?: Date; endDate?: Date } {
        const today = S25Util.date.currentDate();
        switch (filter.timeRange) {
            case "upcoming":
                return { startDate: today };
            case "past":
                return { startDate: S25Util.date.addMonths(today, PAST_DEFAULT_MONTHS), endDate: today };
            case "today":
                return { startDate: today, endDate: today };
            case "week":
                return { startDate: today, endDate: S25Util.date.addWeeks(today, 1) };
            case "month":
                return { startDate: today, endDate: S25Util.date.addMonths(today, 1) };
            case "all":
                return { startDate: new Date(2000, 0, 1) };
            case "custom":
                if (filter.startDate && filter.endDate) {
                    const startDate = S25Util.date.parseDropTZ(filter.startDate);
                    const endDate = S25Util.date.parseDropTZ(filter.endDate);
                    return {
                        startDate,
                        endDate,
                    };
                }
                return {};
            default:
                return {};
        }
    }

    private static buildEventSearchParams(filter: ObjectSearchFilter): Event.WsQueryParams {
        const params: Event.WsQueryParams = {
            category_id: [Category.SystemDefined.StudentEngagement],
            order: "asc",
            sort: "start_date",
            state: [Event.State.Ids.Confirmed, Event.State.Ids.Tentative, Event.State.Ids.Draft],
        };

        if (filter.searchTerm?.trim()) {
            params.event_name = filter.searchTerm.trim();
        }

        const now = S25Util.date.currentDate();
        switch (filter.timeRange) {
            case "upcoming":
                params.start_dt = S25Util.date.toS25ISODateStrStartOfDay(now);
                break;
            case "past":
                params.start_dt = S25Util.date.toS25ISODateStrStartOfDay(
                    S25Util.date.addMonths(now, PAST_DEFAULT_MONTHS),
                );
                params.end_dt = S25Util.date.toS25ISODateStrStartOfDay(now);
                break;
            case "today":
                params.start_dt = S25Util.date.toS25ISODateStrStartOfDay(now);
                params.end_dt = S25Util.date.toS25ISODateStrEndOfDay(now);
                break;
            case "week":
                params.start_dt = S25Util.date.toS25ISODateStrStartOfDay(now);
                params.end_dt = S25Util.date.toS25ISODateStrStartOfDay(S25Util.date.addWeeks(now, 1));
                break;
            case "month":
                params.start_dt = S25Util.date.toS25ISODateStrStartOfDay(now);
                params.end_dt = S25Util.date.toS25ISODateStrStartOfDay(S25Util.date.addMonths(now, 1));
                break;
            case "all":
                params.start_dt = S25Util.date.toS25ISODateStrStartOfDay(new Date(2000, 0, 1));
                break;
            case "custom":
                if (filter.startDate && filter.endDate) {
                    params.start_dt = S25Util.date.toS25ISODateStrStartOfDay(filter.startDate);
                    params.end_dt = S25Util.date.toS25ISODateStrEndOfDay(filter.endDate);
                }
                break;
        }

        const organizationIds =
            filter.organizations
                ?.map((organization) => Number(organization.itemId))
                .filter((organizationId) => !Number.isNaN(organizationId) && organizationId > 0) ?? [];
        if (organizationIds.length) {
            params.organization_id = organizationIds;
        }

        if (filter.eventType === "attending") {
            params.attending = "T";
        }
        if (filter.eventType === "promoted") {
            params.promoted_events = "T";
        }
        if (filter.eventType === "following") {
            params.attendee_state = [attendanceState.INTERESTED];
        }

        return params;
    }

    static simpleOrgSearch(filter: ObjectSearchFilter, options?: { myOrgs?: boolean }): Organization.WsQueryParams {
        const params: Organization.WsQueryParams = { ...SeSearchUtil.baseOrgParams };

        const name = filter.searchTerm?.trim();
        if (name) params.name = name;

        if (!!options?.myOrgs) params.my_orgs = true;

        return params;
    }

    static async buildOrgSearchParams(
        filter: ObjectSearchFilter,
        options?: {
            isAdmin?: boolean;
            myOrgs?: boolean;
        },
    ): Promise<Organization.WsQueryParams | undefined> {
        const hasFilters =
            !!options?.myOrgs ||
            filter.roles?.length ||
            filter.searchTerm ||
            filter.statuses?.length ||
            filter.categories?.length ||
            filter.types?.length;
        if (!hasFilters && !!options?.isAdmin) return;
        if (!options?.isAdmin && !filter.categories?.length && !filter.types?.length && !filter.roles?.length) {
            return Promise.resolve(SeSearchUtil.simpleOrgSearch(filter, options));
        }

        const params: Organization.WsQueryParams = {
            attribute_id: CustomAttributes.system.StudentEngagementOrg.id,
            attribute_value: "T",
        };

        if (!!options?.isAdmin) params.is_admin = true;
        if (!!options?.myOrgs) params.my_orgs = true;

        if (filter.roles?.length) {
            params.role_id = filter.roles.map((role) => role.itemId!);
        }

        const name = filter.searchTerm?.trim();
        if (name) params.name = name;

        if (filter.statuses?.length) {
            params.rating_id = filter.statuses.map((status) => status.itemId!);
        }

        if (filter.categories?.length) {
            params.category_id = filter.categories.map((category) => category.itemId!);
        }

        if (filter.types?.length) {
            params.type_id = filter.types.map((type) => type.itemId!);
        }

        return params;
    }

    private static orgAdvStep(
        stepTypeId: (typeof StepType.Organization)[keyof typeof StepType.Organization],
        stepParam: SearchCriteria.StepParam[],
    ): SearchCriteria.Step {
        const { contact_role_id, ...template } =
            AdvancedSearchUtil.s25SearchAdvancedStepTemplate.organization[stepTypeId];
        return {
            ...template,
            qualifier: 1,
            step_param: stepParam,
        };
    }
}
